var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { customElement, property, state } from 'lit/decorators.js';
/**
 * <vitesse-virtual-list> — render only the rows that are on screen.
 *
 * A scroll box with `max-height` hides rows; it does not stop rendering them. Every row is still in
 * the DOM, in the accessibility tree, and being styled, so a list of twenty-five costs
 * twenty-five and a list of a thousand costs a thousand. That is fine right up until it is not, and
 * the moment it is not is usually in front of a customer.
 *
 * This keeps a WINDOW: the rows that intersect the viewport plus a small overscan, wrapped in two
 * spacers that stand in for everything above and below. The scrollbar is the right size, scrolling is
 * native, and the row count in the DOM is a function of the box's height rather than of the data.
 *
 * FIXED ROW HEIGHT, declared. Measuring every row to support variable heights is a much bigger
 * component and buys nothing here: these lists are uniform by construction, because they are made of
 * one row template. If a list ever needs variable heights, that is the moment to reach for a
 * measuring virtualiser, not before.
 *
 *   <vitesse-virtual-list .items=${rows} .rowHeight=${62} .renderItem=${(r) => html`…`}>
 *   </vitesse-virtual-list>
 *
 * LIGHT DOM, so the host app's row styles apply without piercing a shadow boundary. This renders
 * other people's markup; owning its styling would be the wrong boundary.
 */
let VitesseVirtualList = class VitesseVirtualList extends LitElement {
    constructor() {
        super(...arguments);
        this.items = [];
        /** Row height in px. Must match what the row template actually renders, or the spacers lie and the
         *  scrollbar drifts. */
        this.rowHeight = 56;
        /** How many rows to render beyond each edge. Enough that a fast flick does not show a gap, few
         *  enough that the DOM stays small. */
        this.overscan = 4;
        this.renderItem = () => html ``;
        this.first = 0;
        this.count = 0;
        this.onScroll = () => this.recompute();
    }
    createRenderRoot() {
        return this;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('scroll', this.onScroll, { passive: true });
        // The window depends on the box's height, which changes with the panel, the sidebar and the
        // viewport. Recomputing only on scroll would leave a half-filled list after any resize.
        this.ro = new ResizeObserver(() => this.recompute());
        this.ro.observe(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('scroll', this.onScroll);
        this.ro?.disconnect();
    }
    firstUpdated() {
        this.recompute();
    }
    updated(changed) {
        // A shorter list can leave `first` past the end, which would render nothing at all on a list that
        // clearly has rows. Clamped here rather than guarded at every read.
        if (changed.has('items') || changed.has('rowHeight'))
            this.recompute();
    }
    recompute() {
        const h = this.clientHeight || 1;
        const visible = Math.ceil(h / this.rowHeight);
        const first = Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.overscan);
        const count = Math.min(this.items.length - first, visible + this.overscan * 2);
        if (first !== this.first || count !== this.count) {
            this.first = first;
            this.count = Math.max(0, count);
        }
    }
    render() {
        const first = Math.min(this.first, Math.max(0, this.items.length - 1));
        const slice = this.items.slice(first, first + this.count);
        const above = first * this.rowHeight;
        const below = Math.max(0, (this.items.length - first - slice.length) * this.rowHeight);
        return html `
      <!-- The spacers ARE the scrollbar. They carry the height of everything not rendered, so the
           thumb is the right size and native scrolling does the rest: no transform, no scroll
           hijacking, and the browser's own smoothness.

           OMITTED at zero rather than rendered empty: with no top spacer, the first row really is
           the list's :first-child, so a host's "no divider on the first row" rule keeps working.
           A 0px div would silently steal that position and put a stray line under the box's top
           border. -->
      ${above ? html `<div style="height:${above}px"></div>` : nothing}
      ${this.keyFor
            ? repeat(slice, (item, i) => this.keyFor(item, first + i), (item, i) => this.renderItem(item, first + i))
            : slice.map((item, i) => this.renderItem(item, first + i))}
      ${below ? html `<div style="height:${below}px"></div>` : nothing}
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseVirtualList.prototype, "items", void 0);
__decorate([
    property({ type: Number })
], VitesseVirtualList.prototype, "rowHeight", void 0);
__decorate([
    property({ type: Number })
], VitesseVirtualList.prototype, "overscan", void 0);
__decorate([
    property({ attribute: false })
], VitesseVirtualList.prototype, "renderItem", void 0);
__decorate([
    property({ attribute: false })
], VitesseVirtualList.prototype, "keyFor", void 0);
__decorate([
    state()
], VitesseVirtualList.prototype, "first", void 0);
__decorate([
    state()
], VitesseVirtualList.prototype, "count", void 0);
VitesseVirtualList = __decorate([
    customElement('vitesse-virtual-list')
], VitesseVirtualList);
export { VitesseVirtualList };
//# sourceMappingURL=vitesse-virtual-list.js.map